
import * as React from "react"
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area"

import { cn } from "@/lib/utils"

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
    type?: "auto" | "always" | "scroll" | "hover"
  }
>(({ className, children, type = "auto", ...props }, ref) => {
  // Create a local state for hover tracking if needed
  const [isHovering, setIsHovering] = React.useState(false);
  
  // Calculate whether scrollbar should be visible
  const showScrollbar = type === "always" || 
                        (type === "hover" && isHovering) || 
                        type === "scroll" || 
                        type === "auto";
                        
  // Handlers for hover type scrollbar                    
  const handleMouseEnter = React.useCallback(() => {
    if (type === "hover") setIsHovering(true);
  }, [type]);
  
  const handleMouseLeave = React.useCallback(() => {
    if (type === "hover") setIsHovering(false);
  }, [type]);
  
  return (
    <ScrollAreaPrimitive.Root
      ref={ref}
      className={cn("relative overflow-hidden", className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      // Don't pass the custom type prop to the DOM element
      {...props}
    >
      <ScrollAreaPrimitive.Viewport className="h-full w-full rounded-[inherit]">
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar 
        className={cn(
          type === "auto" ? "opacity-0 transition-opacity duration-200 hover:opacity-100" : 
          showScrollbar ? "opacity-100" : "opacity-0"
        )} 
      />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  )
})
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "flex touch-none select-none transition-opacity duration-200 ease-in-out z-50",
      orientation === "vertical" &&
        "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" &&
        "h-2.5 flex-col border-t border-t-transparent p-[1px]",
      className
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb 
      className={cn(
        "relative flex-1 rounded-full bg-gray-300/50 hover:bg-gray-400/70 transition-colors duration-200 ease-in-out",
        "cursor-pointer"
      )} 
    />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
))
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName

export { ScrollArea, ScrollBar }
